import type { UserActivitiesResult } from '@/queries/profileQueries'

interface ActivityHistoryProps {
  data: UserActivitiesResult | undefined
  isLoading: boolean
  onPageChange: (page: number) => void
}

const ActivityHistory = ({ data, isLoading, onPageChange }: ActivityHistoryProps) => {
  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-success'
    if (score >= 60) return 'text-warning' 
    return 'text-error'
  }

  const getTypeBadge = (type: 'article' | 'book') => {
    return type === 'article' 
      ? 'badge badge-secondary' 
      : 'badge badge-accent'
  }

  const activities = data?.activities || []
  const showEmpty = !isLoading && activities.length === 0

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <div className="flex justify-between items-center mb-4">
          <h2 className="card-title">Quiz History</h2>
          {data && data.totalItems > 0 && (
            <div className="text-sm text-base-content/70">
              {data.totalItems} total activities
            </div>
          )}
        </div>
        
        {isLoading ? (
          <div className="space-y-4">
            <div className="skeleton h-4 w-full"></div>
            <div className="skeleton h-4 w-3/4"></div>
            <div className="skeleton h-4 w-1/2"></div>
          </div>
        ) : showEmpty ? (
          <p className="text-center py-8 text-base-content/70">
            No quiz history yet. Take a quiz to see your scores here!
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Score</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity) => (
                    <tr key={activity.id}>
                      <td>{new Date(activity.date).toISOString().split('T')[0]}</td>
                      <td>
                        <span className={getTypeBadge(activity.type)}>
                          {activity.type}
                        </span>
                      </td>
                      <td className="max-w-xs truncate">
                        {activity.title}
                      </td>
                      <td>
                        <span className={`font-bold ${getScoreColor(activity.score)}`}>
                          {activity.score}%
                        </span>
                      </td>
                      <td>{formatTime(activity.timeSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data && data.totalPages > 1 && (
              <div className="flex justify-center items-center mt-6 gap-2">
                <div className="join">
                  <button 
                    className="join-item btn btn-sm"
                    disabled={!data.hasPrevPage}
                    onClick={() => onPageChange(data.currentPage - 1)}
                  >
                    «
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => {
                    const startPage = Math.max(1, data.currentPage - 2)
                    const page = startPage + i
                    if (page > data.totalPages) return null
                    
                    return (
                      <button
                        key={page}
                        className={`join-item btn btn-sm ${
                          page === data.currentPage ? 'btn-active' : ''
                        }`}
                        onClick={() => onPageChange(page)}
                      >
                        {page}
                      </button>
                    )
                  })}
                  
                  <button 
                    className="join-item btn btn-sm"
                    disabled={!data.hasNextPage}
                    onClick={() => onPageChange(data.currentPage + 1)}
                  >
                    »
                  </button>
                </div>
                
                <div className="text-sm text-base-content/70 ml-4">
                  Page {data.currentPage} of {data.totalPages}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ActivityHistory